import { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";

import { MediaDropZone, MediaInput } from "../../../components/MediaDropZone";

const isImage = (file) => file?.type?.startsWith("image/");

/** Imagen única de un anuncio: zona rosa de la plataforma y, ya elegida, una fila compacta con miniatura. */
export const AnnouncementImageField = ({ file, existingFileLabel, existingFileUrl, onFileSelect, onRemove, title }) => {
  const inputRef = useRef(null);
  const previewUrl = useMemo(() => (isImage(file) ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const pickImage = (files) => {
    const image = Array.from(files || []).find(isImage);
    if (image) onFileSelect(image);
  };

  const name = file?.name || existingFileLabel;
  if (!name) {
    return <MediaDropZone minHeight={96} title={title} onFiles={pickImage} />;
  }

  const src = previewUrl || existingFileUrl;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
      }}
    >
      <Box
        sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: "8px", overflow: "hidden", backgroundColor: "action.hover" }}
      >
        {src && <Box component="img" src={src} alt={name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />}
      </Box>
      <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: "0.875rem" }}>
        {name}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={() => inputRef.current?.click()}
        sx={{ border: "none", background: "none", p: 0, font: "inherit", fontSize: "0.8125rem", color: "text.secondary", textDecoration: "underline", cursor: "pointer" }}
      >
        Cambiar
      </Box>
      <IconButton size="small" aria-label="Quitar imagen" title="Quitar imagen" onClick={onRemove}>
        <X size={16} />
      </IconButton>
      <MediaInput inputRef={inputRef} accept="image/*" onFiles={pickImage} />
    </Box>
  );
};

AnnouncementImageField.propTypes = {
  file: PropTypes.shape({ name: PropTypes.string, type: PropTypes.string }),
  existingFileLabel: PropTypes.string,
  existingFileUrl: PropTypes.string,
  onFileSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
};

export default AnnouncementImageField;
